import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext, useEffect, useMemo, useRef } from 'react';

import { personMessagesChannelName } from '@/channels/utils/channelNames';
import AppContext from '@/contexts/appContext';
import { useChannelData } from '@/hooks/useChannel';
import { Channel } from '@/pages/Timeline/components/MessageEvent/types';
import MessageRepository from '@/repositories/MessageRepository';
import { Message } from '@/types/message';
import PaginatedResponse from '@/types/paginatedResponse';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

/**
 * A person's messages, newest first -- MessageViewSet's queryset carries that
 * ordering, so no caller asks for it.
 */
export const usePersonMessages = (
  personId: string,
  channel?: Channel,
  onChannelUpdate?: (updatedChannel: string) => void,
  params?: Record<string, string>
) => {
  const queryClient = useQueryClient();
  const { addMessage } = useContext(AppContext);
  const messageRepository = MessageRepository();
  const queryKey = useMemo(
    () => ['getMessagesForPerson', personId, channel ?? 'all', params ?? {}],
    [personId, channel, params]
  );

  const queryResponse = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      return await messageRepository.getMessages({
        person_id: personId,
        ...params,
        ...(channel ? { channel } : {}),
        page: String(pageParam)
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: PaginatedResponse<Message[]>) => lastPage.links.next ?? undefined,
    refetchOnWindowFocus: false,
    gcTime: 0 // clear pages on unmount so returning to messages always starts fresh from page 1
  });

  const { error, isError } = queryResponse;

  useEffect(() => {
    if (isError) {
      addMessage(mapAxiosErrorMessages(error as AxiosError));
    }
  }, [addMessage, error, isError]);

  const onChannelUpdateRef = useRef(onChannelUpdate);
  onChannelUpdateRef.current = onChannelUpdate;

  // The websocket payload is a channel-filtered page 1, which can't be spliced
  // into this (possibly unified, multi-channel) query's cache, so refetch instead.
  useChannelData<PaginatedResponse<Message[]>>({
    channelId: personMessagesChannelName(personId),
    channelName: `Messaging for person using ${channel ?? 'all'}`,
    eventName: channel,
    messageCallback: (message) => {
      void queryClient.invalidateQueries({ queryKey });
      if (message.name) onChannelUpdateRef.current?.(message.name);
    }
  });

  return queryResponse;
};
